import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { navigation, topStripServices } from "@/data/siteData";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo-transparent.webp";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Services Strip */}
      <div className="bg-gradient-to-r from-secondary via-secondary to-primary overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-1.5">
          {[...topStripServices, ...topStripServices, ...topStripServices].map((service, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 mx-4 text-xs font-medium text-primary-foreground uppercase tracking-wider">
              <ArrowUpRight className="h-3 w-3" />
              {service}
            </span>
          ))}
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-background border-b border-border">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between py-2.5">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 md:-ml-4">
            <img src={logo} alt="IntelliBPO" className="h-14 md:h-20 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  className={cn(
                    "px-3 py-2.5 text-[13px] font-semibold uppercase tracking-wide transition-colors flex items-center gap-1",
                    location.pathname === item.href
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  )}
                  aria-haspopup={item.children ? "menu" : undefined}
                >
                  {item.name}
                  {item.children && <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
                </Link>
                {item.children && (
                  <div
                    role="menu"
                    className="absolute top-full left-0 hidden group-hover:block group-focus-within:block bg-background shadow-lg rounded-lg border border-border min-w-[280px] py-2 z-50"
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        role="menuitem"
                        className="block px-4 py-2.5 text-sm text-foreground hover:text-primary hover:bg-muted transition-colors"
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-2">
            <Link to="/contact">
              <Button variant="outline" size="sm" className="uppercase text-xs font-semibold tracking-wide">
                <Phone className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Contact Us
              </Button>
            </Link>
            <Link to="/book-a-meeting">
              <Button variant="default" size="sm" className="uppercase text-xs font-semibold tracking-wide bg-gradient-to-r from-secondary to-primary hover:opacity-90">
                Book a Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="xl:hidden p-2 rounded-md text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div id="mobile-navigation" className="xl:hidden bg-background border-t border-border animate-fade-in max-h-[calc(100vh-96px)] overflow-y-auto">
            <div className="container-main py-4 space-y-1">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={cn(
                      "block px-4 py-3 text-sm font-semibold uppercase tracking-wide transition-colors rounded-lg",
                      location.pathname === item.href
                        ? "text-primary bg-primary/5"
                        : "text-foreground hover:text-primary hover:bg-muted"
                    )}
                    onClick={() => {
                      if (!item.children) setMobileMenuOpen(false);
                      if (item.children) setServicesOpen(!servicesOpen);
                    }}
                  >
                    <span className="flex items-center justify-between">
                      {item.name}
                      {item.children && <ChevronDown className={cn("h-4 w-4 transition-transform", servicesOpen && "rotate-180")} />}
                    </span>
                  </Link>
                  {item.children && servicesOpen && (
                    <div className="pl-6 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-muted-foreground hover:text-primary"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-border space-y-2">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button variant="outline" size="lg" className="w-full uppercase text-xs font-semibold tracking-wide">
                    <Phone className="h-3.5 w-3.5 mr-1" />
                    Contact Us
                  </Button>
                </Link>
                <Link to="/book-a-meeting" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button variant="default" size="lg" className="w-full uppercase text-xs font-semibold tracking-wide bg-gradient-to-r from-secondary to-primary hover:opacity-90">
                    Book a Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
